import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBar } from '@angular/material/progress-bar';
import { Colors, Criteria } from './enums';
import { PasswordStrengthValidator, RegExpValidator } from './helpers';
import { PasswordStrengthModel } from './interfaces';

@Component({
  selector: 'mepco-password-strength',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIcon,
    MatProgressBar,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    CommonModule,
  ],
  templateUrl: './password-strength.html',
  styleUrl: './password-strength.scss',
})
export class PasswordStrengthComponent implements OnInit {
  // Inputs
  enableLengthRule = input<boolean>(true);
  enableLowerCaseLetterRule = input<boolean>();
  enableUpperCaseLetterRule = input<boolean>();
  enableDigitRule = input<boolean>();
  enableSpecialCharRule = input<boolean>();
  min = input<number>(8);
  max = input<number>(30);
  customValidator = input<RegExp | string>();
  warnThreshold = input<number>(21);
  accentThreshold = input<number>(81);
  enableScoreInfo = input<boolean>(false);

  formGroup = this.createForm();
  showPassword$ = signal<boolean>(false);
  showConfirmPassword$ = signal<boolean>(false);

  validatorsArray: ValidatorFn[] = [];
  criteriaMap = new Map<Criteria, RegExp>();
  strength$ = signal<number>(0);

  passwordStrengthForm = output<PasswordStrengthModel>();

  private createForm() {
    return new FormGroup(
      {
        password: new FormControl<string>('', {
          nonNullable: true,
          validators: [Validators.required],
        }),
        confirmPassword: new FormControl<string>('', {
          nonNullable: true,
          validators: [Validators.required],
        }),
      },
      { validators: [PasswordStrengthValidator.passwordsMatch('password', 'confirmPassword')] }
    );
  }

  containAtLeastMinChars = signal<boolean>(false);
  containAtLeastOneLowerCaseLetter = signal<boolean>(false);
  containAtLeastOneUpperCaseLetter = signal<boolean>(false);
  containAtLeastOneDigit = signal<boolean>(false);
  containAtLeastOneSpecialChar = signal<boolean>(false);
  containAtCustomChars = signal<boolean>(false);

  private _destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.setRulesAndValidators();

    this.formGroup.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this.calculatePasswordStrength();

      this.passwordStrengthForm.emit({
        password: this.passwordFormControl.value,
        confirmPassword: this.passwordConfirmationFormControl.value,
        valid: this.formGroup.valid,
      });
    });
  }

  togglePasswordVisibility() {
    this.showPassword$.update(show => !show);
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword$.update(show => !show);
  }

  color = computed(() => {
    if (this.strength$() < this.warnThreshold()) {
      return Colors.warn;
    }
    if (this.strength$() < this.accentThreshold()) {
      return Colors.accent;
    }
    return Colors.primary;
  });

  get passwordFormControl(): FormControl {
    return this.formGroup.get('password') as FormControl;
  }

  get passwordConfirmationFormControl(): FormControl {
    return this.formGroup.get('confirmPassword') as FormControl;
  }

  setRulesAndValidators(): void {
    this.validatorsArray = [];
    this.criteriaMap = new Map<Criteria, RegExp>();

    this.validatorsArray.push(Validators.required);
    if (this.enableLengthRule()) {
      this.criteriaMap.set(Criteria.at_min_length, RegExp(`^.{${this.min()},${this.max()}}$`));
      this.validatorsArray.push(Validators.minLength(this.min()));
      this.validatorsArray.push(Validators.maxLength(this.max()));
    }

    if (this.enableLowerCaseLetterRule()) {
      this.criteriaMap.set(Criteria.at_least_one_lower_case_char, RegExpValidator.lowerCase);
      this.validatorsArray.push(Validators.pattern(RegExpValidator.lowerCase));
    }
    if (this.enableUpperCaseLetterRule()) {
      this.criteriaMap.set(Criteria.at_least_one_upper_case_char, RegExpValidator.upperCase);
      this.validatorsArray.push(Validators.pattern(RegExpValidator.upperCase));
    }

    if (this.enableDigitRule()) {
      this.criteriaMap.set(Criteria.at_least_one_digit_char, RegExpValidator.digit);
      this.validatorsArray.push(Validators.pattern(RegExpValidator.digit));
    }
    if (this.enableSpecialCharRule()) {
      this.criteriaMap.set(Criteria.at_least_one_special_char, RegExpValidator.specialChar);
      this.validatorsArray.push(Validators.pattern(RegExpValidator.specialChar));
    }
    if (this.customValidator()) {
      this.criteriaMap.set(Criteria.at_custom_chars, this._parseCustomValidatorsRegex());
      this.validatorsArray.push(Validators.pattern(this._parseCustomValidatorsRegex()));
    }

    this.criteriaMap.forEach((value: any, key: string) => {
      this.validatorsArray.push(PasswordStrengthValidator.validate(key, value));
    });

    this.passwordFormControl.setValidators(Validators.compose([...this.validatorsArray]));

    this.formGroup.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  calculatePasswordStrength(): void {
    const requirements: Array<boolean> = [];
    const unit = 100 / this.criteriaMap.size;

    requirements.push(
      this.enableLengthRule() ? this._containAtLeastMinChars() : false,
      this.enableLowerCaseLetterRule() ? this._containAtLeastOneLowerCaseLetter() : false,
      this.enableUpperCaseLetterRule() ? this._containAtLeastOneUpperCaseLetter() : false,
      this.enableDigitRule() ? this._containAtLeastOneDigit() : false,
      this.enableSpecialCharRule() ? this._containAtLeastOneSpecialChar() : false,
      this.customValidator() ? this._containCustomChars() : false
    );

    this.strength$.set(requirements.filter(v => v).length * unit);
  }

  private _parseCustomValidatorsRegex(): RegExp {
    const value = this.customValidator();

    if (value === undefined) {
      return /$.^/;
    }
    if (value instanceof RegExp) {
      return value;
    }
    if (typeof value === 'string') {
      return new RegExp(value);
    }
    return /(?:)/;
  }

  private _containAtLeastMinChars(): boolean {
    this.containAtLeastMinChars.set(this.passwordFormControl.value.length >= this.min());
    return this.containAtLeastMinChars();
  }

  private _containAtLeastOneLowerCaseLetter(): boolean {
    const criteria = this.criteriaMap.get(Criteria.at_least_one_lower_case_char);
    this.containAtLeastOneLowerCaseLetter.set(criteria ? criteria.test(this.passwordFormControl.value) : false);
    return this.containAtLeastOneLowerCaseLetter();
  }

  private _containAtLeastOneUpperCaseLetter(): boolean {
    const criteria = this.criteriaMap.get(Criteria.at_least_one_upper_case_char);
    this.containAtLeastOneUpperCaseLetter.set(criteria ? criteria.test(this.passwordFormControl.value) : false);
    return this.containAtLeastOneUpperCaseLetter();
  }

  private _containAtLeastOneDigit(): boolean {
    const criteria = this.criteriaMap.get(Criteria.at_least_one_digit_char);
    this.containAtLeastOneDigit.set(criteria ? criteria.test(this.passwordFormControl.value) : false);
    return this.containAtLeastOneDigit();
  }

  private _containAtLeastOneSpecialChar(): boolean {
    const criteria = this.criteriaMap.get(Criteria.at_least_one_special_char);
    this.containAtLeastOneSpecialChar.set(criteria ? criteria.test(this.passwordFormControl.value) : false);
    return this.containAtLeastOneSpecialChar();
  }

  private _containCustomChars(): boolean {
    const criteria = this.criteriaMap.get(Criteria.at_custom_chars);
    this.containAtCustomChars.set(criteria ? criteria.test(this.passwordFormControl.value) : false);
    return this.containAtCustomChars();
  }
}
